import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Loader2, CheckCircle2, XCircle, ExternalLink, FileText, Building2, AlertTriangle, Settings, Calendar, RefreshCcw, Mail } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "../lib/format";

export default function Integrations() {
  const [params, setParams] = useSearchParams();
  const [ficStatus, setFicStatus] = useState(null);
  const [companies, setCompanies] = useState(null);
  const [gcalStatus, setGcalStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [fRes, gRes] = await Promise.allSettled([
        api.get("/fic/status"),
        api.get("/gcal/status"),
      ]);
      if (fRes.status === "fulfilled") {
        setFicStatus(fRes.value.data);
        if (fRes.value.data.connected) {
          try { const c = await api.get("/fic/companies"); setCompanies(c.data); }
          catch { setCompanies([]); }
        }
      }
      if (gRes.status === "fulfilled") setGcalStatus(gRes.value.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const toasts = [
      ["fic_connected", "success", "Fatture in Cloud collegato"],
      ["fic_error", "error", "Collegamento FiC fallito"],
      ["gcal_connected", "success", "Google Calendar collegato"],
      ["gcal_error", "error", "Collegamento Google Calendar fallito"],
    ];
    let changed = false;
    toasts.forEach(([key, kind, msg]) => {
      const v = params.get(key);
      if (v) {
        const text = kind === "error" ? `${msg}: ${v}` : msg;
        (toast)[kind](text);
        params.delete(key);
        changed = true;
      }
    });
    if (changed) setParams(params, { replace: true });
     
  }, [params]);

  // FiC actions
  const ficConnect = async () => {
    setActing(true);
    try {
      const r = await api.get("/fic/connect");
      window.location.href = r.data.authorize_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore avvio OAuth");
      setActing(false);
    }
  };
  const ficDisconnect = async () => {
    if (!window.confirm("Scollegare Fatture in Cloud? Non emetterai più fatture elettroniche dal CRM.")) return;
    setActing(true);
    try { await api.delete("/fic/disconnect"); toast.success("Scollegato"); await load(); }
    catch { toast.error("Errore"); }
    finally { setActing(false); }
  };
  const ficSelectCompany = async (fic_company_id) => {
    setActing(true);
    try { await api.post("/fic/select-company", { fic_company_id }); toast.success("Company aggiornata"); await load(); }
    catch { toast.error("Errore selezione company"); }
    finally { setActing(false); }
  };

  // Google Calendar actions
  const gcalConnect = async () => {
    setActing(true);
    try {
      const r = await api.get("/gcal/connect");
      window.location.href = r.data.authorize_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore avvio OAuth Google");
      setActing(false);
    }
  };
  const gcalDisconnect = async () => {
    if (!window.confirm("Scollegare Google Calendar? Gli appuntamenti già sincronizzati rimarranno nel tuo calendario Google.")) return;
    setActing(true);
    try { await api.delete("/gcal/disconnect"); toast.success("Scollegato"); await load(); }
    catch { toast.error("Errore"); }
    finally { setActing(false); }
  };
  const gcalSyncAll = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/gcal/sync-all");
      toast.success(`Sincronizzati ${r.data.created} appuntamenti su Google Calendar`);
      if (r.data.errors?.length) toast.error(`${r.data.errors.length} errori di sync`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore sync");
    } finally { setSyncing(false); }
  };

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const gcalRedirect = `${window.location.origin}/api/gcal/callback`;
  const ficRedirect = `${window.location.origin}/api/fic/callback`;

  return (
    <div className="p-8 lg:p-10 space-y-8 fade-up max-w-4xl">
      <header>
        <div className="mono-label flex items-center gap-1.5"><Settings className="h-3 w-3" /> Impostazioni</div>
        <h1 className="font-display text-4xl font-medium text-neutral-900 mt-1">Integrazioni</h1>
        <p className="text-sm text-neutral-500 mt-2">Collega servizi esterni per automatizzare fatturazione, calendario, firma digitale.</p>
      </header>

      {/* Google Calendar */}
      <div className="border border-neutral-200 bg-white" data-testid="gcal-card">
        <div className="p-6 border-b border-neutral-200 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 flex items-center justify-center bg-blue-600">
              <Calendar className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="mono-label">Calendario</div>
              <h2 className="font-display text-xl font-medium">Google Calendar</h2>
              <p className="text-sm text-neutral-500 mt-1">Sincronizza l'Agenda amdirDesk con il tuo Google Calendar: ogni sopralluogo, riunione o scadenza finisce automaticamente nel tuo calendario.</p>
            </div>
          </div>
          <div>
            {gcalStatus?.connected ? (
              <span className="inline-flex items-center gap-1.5 font-mono text-xs font-semibold px-2 py-1 border border-green-200 bg-green-50 text-green-700" data-testid="gcal-status-connected">
                <CheckCircle2 className="h-3 w-3" /> COLLEGATO
              </span>
            ) : gcalStatus?.configured ? (
              <span className="inline-flex items-center gap-1.5 font-mono text-xs font-semibold px-2 py-1 border border-neutral-200 bg-neutral-50 text-neutral-600" data-testid="gcal-status-ready">
                PRONTO
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 font-mono text-xs font-semibold px-2 py-1 border border-amber-200 bg-amber-50 text-amber-700" data-testid="gcal-status-notconfigured">
                <AlertTriangle className="h-3 w-3" /> DA CONFIGURARE
              </span>
            )}
          </div>
        </div>

        <div className="p-6">
          {!gcalStatus?.configured && (
            <div className="border-l-2 border-amber-400 bg-amber-50 p-4 text-sm space-y-2" data-testid="gcal-setup-instructions">
              <div className="font-medium text-amber-900">Setup richiesto — crea le credenziali OAuth Google</div>
              <ol className="list-decimal list-inside space-y-1.5 text-amber-900">
                <li>Vai su <a href="https://console.cloud.google.com" target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-0.5">console.cloud.google.com <ExternalLink className="h-3 w-3" /></a> e crea un nuovo progetto (es. <code>amdirDesk</code>)</li>
                <li>Menu <strong>API e Servizi → Libreria</strong> → cerca <strong>Google Calendar API</strong> → clicca <strong>Abilita</strong></li>
                <li>Menu <strong>API e Servizi → Schermata consenso OAuth</strong> → tipo <strong>Esterno</strong> → compila nome app, email supporto. Negli <strong>Scopes</strong> aggiungi <code>.../auth/calendar</code>. In <strong>Utenti di test</strong> aggiungi <code>eugenio.giovannelli@gmail.com</code>.</li>
                <li>Menu <strong>API e Servizi → Credenziali</strong> → <strong>Crea credenziali → ID client OAuth</strong> → tipo <strong>Applicazione web</strong></li>
                <li>In <strong>URI di reindirizzamento autorizzati</strong> incolla esattamente:
                  <div className="mt-1 font-mono text-xs bg-white border border-amber-200 p-2 break-all" data-testid="gcal-redirect-uri">{gcalRedirect}</div>
                </li>
                <li>Clicca <strong>Crea</strong>, copia <strong>Client ID</strong> e <strong>Client Secret</strong>, incollameli in chat</li>
              </ol>
            </div>
          )}

          {gcalStatus?.configured && !gcalStatus?.connected && (
            <div className="space-y-3">
              <p className="text-sm text-neutral-600">
                Tutto pronto. Cliccando sarai mandato su Google per autorizzare amdirDesk a scrivere nel tuo calendario.
              </p>
              <Button onClick={gcalConnect} disabled={acting} data-testid="gcal-connect-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
                {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Connetti Google Calendar <ExternalLink className="h-3.5 w-3.5 ml-1.5" /></>}
              </Button>
            </div>
          )}

          {gcalStatus?.connected && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="mono-label">Account Google</div>
                  <div className="font-mono text-sm font-semibold mt-1 flex items-center gap-1.5">
                    <Mail className="h-3.5 w-3.5" />
                    {gcalStatus.google_email || "—"}
                  </div>
                </div>
                <div>
                  <div className="mono-label">Calendario</div>
                  <div className="font-mono text-sm mt-1">{gcalStatus.calendar_id}</div>
                </div>
              </div>

              <div className="border-l-2 border-blue-400 bg-blue-50 p-4 text-xs text-blue-900">
                Da ora, ogni nuovo appuntamento su amdirDesk sarà creato anche su Google. Modifiche e cancellazioni sono sincronizzate automaticamente.
              </div>

              <div className="pt-3 border-t border-neutral-200 flex items-center gap-2 flex-wrap">
                <Button variant="outline" onClick={gcalSyncAll} disabled={syncing} data-testid="gcal-sync-all-btn">
                  {syncing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCcw className="h-4 w-4 mr-1.5" />}
                  Sync appuntamenti esistenti
                </Button>
                <Button variant="outline" onClick={gcalDisconnect} disabled={acting} data-testid="gcal-disconnect-btn">
                  <XCircle className="h-4 w-4 mr-1.5" /> Scollega
                </Button>
                <p className="text-xs text-neutral-500">Scollegando, gli eventi già creati su Google rimangono.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fatture in Cloud */}
      <div className="border border-neutral-200 bg-white" data-testid="fic-card">
        <div className="p-6 border-b border-neutral-200 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 flex items-center justify-center brand-bg">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="mono-label">Fatturazione elettronica</div>
              <h2 className="font-display text-xl font-medium">Fatture in Cloud</h2>
              <p className="text-sm text-neutral-500 mt-1">Emetti fatture elettroniche B2B e PA direttamente dal CRM. Firma e invio a SdI gestiti da FiC.</p>
            </div>
          </div>
          <div>
            {ficStatus?.connected ? (
              <span className="inline-flex items-center gap-1.5 font-mono text-xs font-semibold px-2 py-1 border border-green-200 bg-green-50 text-green-700" data-testid="fic-status-connected">
                <CheckCircle2 className="h-3 w-3" /> COLLEGATO
              </span>
            ) : ficStatus?.configured ? (
              <span className="inline-flex items-center gap-1.5 font-mono text-xs font-semibold px-2 py-1 border border-neutral-200 bg-neutral-50 text-neutral-600" data-testid="fic-status-ready">
                PRONTO
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 font-mono text-xs font-semibold px-2 py-1 border border-amber-200 bg-amber-50 text-amber-700" data-testid="fic-status-notconfigured">
                <AlertTriangle className="h-3 w-3" /> DA CONFIGURARE
              </span>
            )}
          </div>
        </div>

        <div className="p-6">
          {!ficStatus?.configured && (
            <div className="border-l-2 border-amber-400 bg-amber-50 p-4 text-sm space-y-2" data-testid="fic-setup-instructions">
              <div className="font-medium text-amber-900">Setup richiesto — serve una tua azione</div>
              <ol className="list-decimal list-inside space-y-1 text-amber-900">
                <li>Vai su <a href="https://developers.fattureincloud.it/" target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-0.5">developers.fattureincloud.it <ExternalLink className="h-3 w-3" /></a> e accedi con il tuo account FiC</li>
                <li>Clicca <strong>Impostazioni → Sviluppatore → Nuova app</strong></li>
                <li>Come <strong>Redirect URI</strong> inserisci esattamente:
                  <div className="mt-1 font-mono text-xs bg-white border border-amber-200 p-2 break-all" data-testid="fic-redirect-uri">{ficRedirect}</div>
                </li>
                <li>Salva, copia <strong>Client ID</strong> e <strong>Client Secret</strong></li>
                <li>Incollameli in chat o aggiungili alle variabili d'ambiente <code>FIC_CLIENT_ID</code> e <code>FIC_CLIENT_SECRET</code></li>
              </ol>
            </div>
          )}

          {ficStatus?.configured && !ficStatus?.connected && (
            <div className="space-y-3">
              <p className="text-sm text-neutral-600">
                Pronti al collegamento. Cliccando sarai mandato su Fatture in Cloud per autorizzare amdirDesk a emettere fatture per conto tuo.
              </p>
              <Button onClick={ficConnect} disabled={acting} data-testid="fic-connect-btn" className="brand-bg hover:opacity-90">
                {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Connetti Fatture in Cloud <ExternalLink className="h-3.5 w-3.5 ml-1.5" /></>}
              </Button>
            </div>
          )}

          {ficStatus?.connected && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="mono-label">Company attiva</div>
                  <div className="font-mono text-sm font-semibold mt-1 flex items-center gap-1.5">
                    <Building2 className="h-3.5 w-3.5" />
                    {ficStatus.fic_company_name || "— nessuna"}
                    {ficStatus.fic_company_id && <span className="font-normal text-neutral-500">#{ficStatus.fic_company_id}</span>}
                  </div>
                </div>
                <div>
                  <div className="mono-label">Collegato il</div>
                  <div className="font-mono text-sm mt-1">{fmtDate(ficStatus.created_at)}</div>
                </div>
              </div>

              {companies && companies.length > 1 && (
                <div>
                  <div className="mono-label mb-2">Cambia company</div>
                  <div className="space-y-1" data-testid="fic-company-list">
                    {companies.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => ficSelectCompany(c.id)}
                        disabled={acting || c.id === ficStatus.fic_company_id}
                        data-testid={`fic-company-${c.id}`}
                        className={`w-full text-left p-2.5 border text-sm flex items-center gap-2 ${
                          c.id === ficStatus.fic_company_id
                            ? "brand-border bg-neutral-50 cursor-default"
                            : "border-neutral-200 hover:border-neutral-400 bg-white"
                        }`}
                      >
                        <Building2 className="h-3.5 w-3.5" />
                        <span className="flex-1">{c.name}</span>
                        {c.tax_code && <span className="font-mono text-xs text-neutral-500">{c.tax_code}</span>}
                        {c.id === ficStatus.fic_company_id && <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="pt-3 border-t border-neutral-200 flex items-center gap-2">
                <Button variant="outline" onClick={ficDisconnect} disabled={acting} data-testid="fic-disconnect-btn">
                  <XCircle className="h-4 w-4 mr-1.5" /> Scollega
                </Button>
                <p className="text-xs text-neutral-500">Puoi scollegare in qualsiasi momento: lo storico delle fatture rimane in FiC.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
